import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { loc } from "../lib/localize";
import { sideColor } from "../lib/odds";
import { Input } from "../components/ui/input";
import { Search, FileQuestion } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WikiIndex() {
  const { t, lang } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("__all__");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles").then(({ data }) => setArticles(data)).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(articles.map((a) => loc(a, "category", lang)));
    return ["__all__", ...Array.from(set)];
  }, [articles, lang]);

  const filtered = articles.filter((a) => {
    const okCat = cat === "__all__" || loc(a, "category", lang) === cat;
    const okQ = !q || loc(a, "title", lang).toLowerCase().includes(q.toLowerCase());
    return okCat && okQ;
  });

  return (
    <main className="relative z-10 mx-auto max-w-7xl px-6 py-14 md:px-10">
      <div className="mb-10">
        <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("wiki.eyebrow")}</span>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("wiki.title")}</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">{t("wiki.sub")}</p>
      </div>

      <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="wiki-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("wiki.searchPlaceholder")}
            className="border-white/15 bg-card pl-10"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`wiki-cat-${c}`}
              onClick={() => setCat(c)}
              className={`border px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                cat === c ? "border-primary bg-primary/10 text-primary" : "border-white/15 text-muted-foreground hover:text-foreground"
              }`}
            >
              {c === "__all__" ? t("landing.all") : c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">{t("common.loading")}</p>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-4 border border-dashed border-white/15 py-24 text-center">
          <FileQuestion className="h-10 w-10 text-muted-foreground" />
          <p className="text-muted-foreground">{t("wiki.none")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <Link
              key={a.id}
              to={lang === "en" ? `/en/wiki/${a.slug_en || a.slug}` : `/wiki/${a.slug}`}
              data-testid="wiki-article-card"
              className="group flex flex-col justify-between bg-card p-6 transition-colors hover:bg-[#1E1E1E]"
            >
              <div>
                {a.image_url && (
                  <img src={`${BACKEND_URL}${a.image_url}`} alt="" className="mb-4 h-40 w-full border border-white/10 object-cover" />
                )}
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{loc(a, "category", lang)}</span>
                <h3 className="mt-2 font-heading text-lg font-bold leading-tight">{loc(a, "title", lang)}</h3>
                {loc(a, "summary", lang) && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{loc(a, "summary", lang)}</p>}
              </div>
              <div className="mt-6 flex items-end justify-between border-t border-white/10 pt-4">
                <div className="font-mono text-[11px] text-muted-foreground">
                  {a.bet_count} {t("wiki.bets")}
                </div>
                <div className="text-right">
                  <span className={`font-heading text-3xl font-black ${sideColor(a.multiplier_side)}`}>{a.headline_multiplier}×</span>
                  <p className={`font-mono text-[10px] uppercase tracking-widest ${sideColor(a.multiplier_side)}`}>
                    {a.multiplier_side === "agree" ? t("article.agree") : t("article.disagree")}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
